import { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { lazyWithReload } from './lib/utils/lazy-with-reload';
import { MarketingLayout } from './components/layout/MarketingLayout';
import { Login } from './routes/Login';
import { Signup } from './routes/Signup';
import { ForgotPassword } from './routes/ForgotPassword';
import { ResetPassword } from './routes/ResetPassword';
import { AuthCallback } from './routes/AuthCallback';
import { ProtectedRoute } from './components/layout/ProtectedRoute';
import { AcceptInvite } from './routes/AcceptInvite';
import { DashboardLayout } from './components/layout/DashboardLayout';
// Eager-loaded: only the dashboard landing — it owns first-paint.
import { CommandCenter } from './routes/hubs/CommandCenter';
// Lazy-loaded: everything else, including the other hubs (recharts-heavy) and
// the marketing home (gsap + framer-motion + scroll walkthrough) so dashboard
// users never download the marketing site and vice versa.
const MarketingHome = lazyWithReload(() => import('./routes/MarketingHome').then(m => ({ default: m.MarketingHome })));
const Revenue = lazyWithReload(() => import('./routes/hubs/Revenue').then(m => ({ default: m.Revenue })));
const Clients = lazyWithReload(() => import('./routes/hubs/Clients').then(m => ({ default: m.Clients })));
const DataHealth = lazyWithReload(() => import('./routes/hubs/DataHealth').then(m => ({ default: m.DataHealth })));
const Intelligence = lazyWithReload(() => import('./routes/hubs/Intelligence').then(m => ({ default: m.Intelligence })));
const Actions = lazyWithReload(() => import('./routes/hubs/Actions').then(m => ({ default: m.Actions })));
const Settings = lazyWithReload(() => import('./routes/hubs/Settings').then(m => ({ default: m.Settings })));
const BuildProgress = lazyWithReload(() => import('./routes/BuildProgress').then(m => ({ default: m.BuildProgress })));
const ClientDetail = lazyWithReload(() => import('./routes/hubs/ClientDetail').then(m => ({ default: m.ClientDetail })));
const Marketing = lazyWithReload(() => import('./routes/hubs/Marketing').then(m => ({ default: m.Marketing })));
const Audit = lazyWithReload(() => import('./routes/hubs/Audit').then(m => ({ default: m.Audit })));
const Trust = lazyWithReload(() => import('./routes/hubs/Trust').then(m => ({ default: m.Trust })));
const MobileBriefing = lazyWithReload(() => import('./routes/MobileBriefing').then(m => ({ default: m.MobileBriefing })));
const ModelInsights = lazyWithReload(() => import('./routes/hubs/ModelInsights').then(m => ({ default: m.ModelInsights })));
const OpsDashboard = lazyWithReload(() => import('./routes/hubs/OpsDashboard').then(m => ({ default: m.OpsDashboard })));
const VerticalLanding = lazyWithReload(() => import('./routes/VerticalLanding').then(m => ({ default: m.VerticalLanding })));
const Contracts = lazyWithReload(() => import('./routes/hubs/Contracts').then(m => ({ default: m.Contracts })));
const AgentPlans = lazyWithReload(() => import('./routes/hubs/AgentPlans').then(m => ({ default: m.AgentPlans })));
const Alerts = lazyWithReload(() => import('./routes/hubs/Alerts').then(m => ({ default: m.Alerts })));
const Meetings = lazyWithReload(() => import('./routes/hubs/Meetings').then(m => ({ default: m.Meetings })));
const Pricing = lazyWithReload(() => import('./routes/Pricing').then(m => ({ default: m.Pricing })));
const Terms = lazyWithReload(() => import('./routes/Terms').then(m => ({ default: m.Terms })));
const Privacy = lazyWithReload(() => import('./routes/Privacy').then(m => ({ default: m.Privacy })));
const Demo = lazyWithReload(() => import('./routes/marketing/Demo').then(m => ({ default: m.Demo })));
const InstantReport = lazyWithReload(() => import('./routes/marketing/InstantReport').then(m => ({ default: m.InstantReport })));
// Product pages — /platform/owner, /platform/sales (docs/PRODUCT_LINES.md)
const ProductPage = lazyWithReload(() => import('./routes/marketing/ProductPage').then(m => ({ default: m.ProductPage })));
const NotFound = lazyWithReload(() => import('./routes/NotFound').then(m => ({ default: m.NotFound })));
const Status = lazyWithReload(() => import('./routes/Status').then(m => ({ default: m.Status })));
const Roadmap = lazyWithReload(() => import('./routes/Roadmap').then(m => ({ default: m.Roadmap })));
const Kpis = lazyWithReload(() => import('./routes/hubs/Kpis').then(m => ({ default: m.Kpis })));
const Upgrade = lazyWithReload(() => import('./routes/Upgrade').then(m => ({ default: m.Upgrade })));

// Minimal fallback shown while lazy chunks load. Keeps the layout from collapsing.
const LazyFallback = () => (
  <div className="flex h-[60vh] items-center justify-center text-2xs font-mono uppercase tracking-widest text-ink-dim">
    <span className="mr-2 h-2 w-2 animate-pulse rounded-full bg-primary-bright" />
    Loading…
  </div>
);
const L = ({ children }: { children: React.ReactNode }) => <Suspense fallback={<LazyFallback />}>{children}</Suspense>;
import { ViewScopeProvider } from './lib/hooks/view-scope';
import { OwnerScopeDefault } from './components/OwnerScopeDefault';
import { PlatformGuard } from './components/PlatformGuard';
import { ImpersonationProvider } from './lib/impersonation';
import { ErrorBoundary, RouteErrorBoundary } from './components/layout/ErrorBoundary';
import { OfflineBanner } from './components/layout/OfflineBanner';
import { useSemanticSearchHotkey } from './components/forms/SemanticSearchModal';
import { useMe, useMeLoaded } from './lib/hooks/use-me';

/**
 * TaxGate — blocks the /tax route tree for workspaces without the Tax add-on
 * (me.features.tax, backed by accounts.tax_enabled). Redirects to /dashboard
 * once /v1/me has resolved; while it loads we render children (the /v1/tax API
 * 403s regardless) so an enabled workspace never flashes a redirect. The API is
 * the hard enforcement — this is the UX layer.
 */
/** SowGate — same shape as TaxGate for the SOW Desk add-on (me.features.sow). */
function SowGate({ children }: { children: React.ReactNode }) {
  const loaded = useMeLoaded();
  const me = useMe();
  if (loaded && me.features?.sow !== true) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

/**
 * HiddenFromPartners — route gate for the surfaces a design-partner workspace does not get
 * (me.features.partner, backed by accounts.partner_surface): Intelligence, Marketing, Model
 * Insights. Same shape as TaxGate, inverted. Children render while /v1/me loads; the API 403s
 * these routes for a partner regardless (hideFromPartners), so this is the UX layer.
 */
function HiddenFromPartners({ children, to = '/dashboard' }: { children: React.ReactNode; to?: string }) {
  const loaded = useMeLoaded();
  const me = useMe();
  if (loaded && me.features?.partner === true) {
    return <Navigate to={to} replace />;
  }
  return <>{children}</>;
}

function TaxGate({ children }: { children: React.ReactNode }) {
  const loaded = useMeLoaded();
  const me = useMe();
  if (loaded && me.features?.tax !== true) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

// Lazy-loaded — only needed when the user opens search (Cmd+K).
const SemanticSearchModal = lazyWithReload(() => import('./components/forms/SemanticSearchModal').then(m => ({ default: m.SemanticSearchModal })));
// Sales Platform
const SalesLayout = lazyWithReload(() => import('./components/layout/SalesLayout').then(m => ({ default: m.SalesLayout })));
const SalesHome = lazyWithReload(() => import('./routes/sales/SalesHome').then(m => ({ default: m.SalesHome })));
const SalesPipeline = lazyWithReload(() => import('./routes/sales/SalesPipeline').then(m => ({ default: m.SalesPipeline })));
const SalesDealRoom = lazyWithReload(() => import('./routes/sales/SalesDealRoom').then(m => ({ default: m.SalesDealRoom })));
const SalesMeetings = lazyWithReload(() => import('./routes/sales/SalesMeetings').then(m => ({ default: m.SalesMeetings })));
const SalesCoaching = lazyWithReload(() => import('./routes/sales/SalesCoaching').then(m => ({ default: m.SalesCoaching })));
const SalesForecast = lazyWithReload(() => import('./routes/sales/SalesForecast').then(m => ({ default: m.SalesForecast })));
const SalesLeaderboard = lazyWithReload(() => import('./routes/sales/SalesLeaderboard').then(m => ({ default: m.SalesLeaderboard })));
const SalesSettings = lazyWithReload(() => import('./routes/sales/SalesSettings').then(m => ({ default: m.SalesSettings })));
const SalesCalls = lazyWithReload(() => import('./routes/sales/SalesCalls').then(m => ({ default: m.SalesCalls })));
const SalesLeads = lazyWithReload(() => import('./routes/sales/SalesLeads').then(m => ({ default: m.SalesLeads })));
const SalesBookFix = lazyWithReload(() => import('./routes/sales/SalesBookFix').then(m => ({ default: m.SalesBookFix })));
// Tax Platform
const TaxLayout = lazyWithReload(() => import('./components/layout/TaxLayout').then(m => ({ default: m.TaxLayout })));
const TaxHome = lazyWithReload(() => import('./routes/tax/TaxHome').then(m => ({ default: m.TaxHome })));
const TaxClients = lazyWithReload(() => import('./routes/tax/TaxClients').then(m => ({ default: m.TaxClients })));
const TaxClientDetail = lazyWithReload(() => import('./routes/tax/TaxClientDetail').then(m => ({ default: m.TaxClientDetail })));
const TaxMeetings = lazyWithReload(() => import('./routes/tax/TaxMeetings').then(m => ({ default: m.TaxMeetings })));
const TaxFollowUps = lazyWithReload(() => import('./routes/tax/TaxFollowUps').then(m => ({ default: m.TaxFollowUps })));
const TaxDeadlines = lazyWithReload(() => import('./routes/tax/TaxDeadlines').then(m => ({ default: m.TaxDeadlines })));
const TaxPlanning = lazyWithReload(() => import('./routes/tax/TaxPlanning').then(m => ({ default: m.TaxPlanning })));
const TaxIntelligence = lazyWithReload(() => import('./routes/tax/TaxIntelligence').then(m => ({ default: m.TaxIntelligence })));
const TaxSettings = lazyWithReload(() => import('./routes/tax/TaxSettings').then(m => ({ default: m.TaxSettings })));
const TaxTime = lazyWithReload(() => import('./routes/tax/TaxTime').then(m => ({ default: m.TaxTime })));
// SOW Desk Platform (fifth platform — gated behind accounts.sow_enabled)
const SowLayout = lazyWithReload(() => import('./components/layout/SowLayout').then(m => ({ default: m.SowLayout })));
const SowHome = lazyWithReload(() => import('./routes/sow/SowHome').then(m => ({ default: m.SowHome })));
const SowRequests = lazyWithReload(() => import('./routes/sow/SowRequests').then(m => ({ default: m.SowRequests })));
const SowRequestDetail = lazyWithReload(() => import('./routes/sow/SowRequestDetail').then(m => ({ default: m.SowRequestDetail })));
const SowTemplates = lazyWithReload(() => import('./routes/sow/SowTemplates').then(m => ({ default: m.SowTemplates })));
const SowRules = lazyWithReload(() => import('./routes/sow/SowRules').then(m => ({ default: m.SowRules })));
const SowIntelligence = lazyWithReload(() => import('./routes/sow/SowIntelligence').then(m => ({ default: m.SowIntelligence })));
const SowSettings = lazyWithReload(() => import('./routes/sow/SowSettings').then(m => ({ default: m.SowSettings })));
const SowStart = lazyWithReload(() => import('./routes/sow/SowStart').then(m => ({ default: m.SowStart })));
// Product front doors — /sales/start and /clients/start (same mechanism as /sow/start)
const ProductStart = lazyWithReload(() => import('./routes/public/ProductStart').then(m => ({ default: m.ProductStart })));
const SowClauses = lazyWithReload(() => import('./routes/sow/SowClauses').then(m => ({ default: m.SowClauses })));
// Wave 3 — template conversion, past-SOW library, and the public rep request form
const ConvertTemplate = lazyWithReload(() => import('./routes/sow/ConvertTemplate').then(m => ({ default: m.ConvertTemplate })));
const SowLibrary = lazyWithReload(() => import('./routes/sow/SowLibrary').then(m => ({ default: m.SowLibrary })));
const SowRequestForm = lazyWithReload(() => import('./routes/public/SowRequestForm').then(m => ({ default: m.SowRequestForm })));
// Wave 3b — renewal radar, rep drill-down, public status page, in-app guide
const SowRenewals = lazyWithReload(() => import('./routes/sow/SowRenewals').then(m => ({ default: m.SowRenewals })));
const SowRepDetail = lazyWithReload(() => import('./routes/sow/SowRepDetail').then(m => ({ default: m.SowRepDetail })));
const SowHelp = lazyWithReload(() => import('./routes/sow/SowHelp').then(m => ({ default: m.SowHelp })));
const SowStatusPage = lazyWithReload(() => import('./routes/public/SowStatusPage').then(m => ({ default: m.SowStatusPage })));
// Clients Platform — the third first-class platform (Owner · Sales · Clients).
// Reuses the owner dashboard's client-facing hub pages (Clients roster,
// ClientDetail, Meetings, Actions, DataHealth) inside ClientsLayout; only the
// pulse/intelligence/settings surfaces are platform-specific.
const ClientsLayout = lazyWithReload(() => import('./components/layout/ClientsLayout').then(m => ({ default: m.ClientsLayout })));
const ClientsHome = lazyWithReload(() => import('./routes/clients/ClientsHome').then(m => ({ default: m.ClientsHome })));
const ClientsIntelligence = lazyWithReload(() => import('./routes/clients/ClientsIntelligence').then(m => ({ default: m.ClientsIntelligence })));
const ClientsSettings = lazyWithReload(() => import('./routes/clients/ClientsSettings').then(m => ({ default: m.ClientsSettings })));

export default function App() {
  const search = useSemanticSearchHotkey();
  return (
    <ErrorBoundary>
    <OfflineBanner />
    <ImpersonationProvider>
    <ViewScopeProvider>
    <OwnerScopeDefault />
    <PlatformGuard />
    <Suspense fallback={null}>
      <SemanticSearchModal open={search.open} onClose={() => search.setOpen(false)} />
    </Suspense>
    <Routes>
      {/* Public — marketing pages share nav/footer via MarketingLayout */}
      <Route element={<MarketingLayout />}>
        <Route path="/" element={<L><MarketingHome /></L>} />
        <Route path="/for/:slug" element={<L><VerticalLanding /></L>} />
        <Route path="/status" element={<L><Status /></L>} />
        <Route path="/roadmap" element={<L><Roadmap /></L>} />
        <Route path="/pricing" element={<L><Pricing /></L>} />
        <Route path="/terms" element={<L><Terms /></L>} />
        <Route path="/privacy" element={<L><Privacy /></L>} />
        <Route path="/demo" element={<L><Demo /></L>} />
        <Route path="/instant-report" element={<L><InstantReport /></L>} />
        {/* Product pages — under /platform so they never collide with the
            protected in-app /sales and /clients trees. Clients, Tax and SOW
            Desk aren't sold self-serve, so they have no marketing page. */}
        <Route path="/platform/owner" element={<L><ProductPage product="owner" /></L>} />
        <Route path="/platform/sales" element={<L><ProductPage product="sales" /></L>} />
        <Route path="/platform/clients" element={<Navigate to="/pricing" replace />} />
      </Route>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      {/* Product front doors — mark the signup as a standalone workspace for one
          product (docs/PRODUCT_LINES.md). Public; the /sales and /clients app
          trees below are protected and never match these paths. */}
      <Route path="/sales/start" element={<L><ProductStart product="sales" /></L>} />
      <Route path="/clients/start" element={<L><ProductStart product="clients" /></L>} />
      <Route path="/sow/start" element={<L><SowStart /></L>} />
      {/* Public rep-facing request form — token-addressed, no auth (see routes/public-sow.ts) */}
      <Route path="/r/:token" element={<L><SowRequestForm /></L>} />
      {/* Public read-only request status — per-request token (see routes/public-sow-status.ts) */}
      <Route path="/s/:token" element={<L><SowStatusPage /></L>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />

      {/* Hub-based dashboard */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<RouteErrorBoundary><CommandCenter /></RouteErrorBoundary>} />
        <Route path="revenue" element={<RouteErrorBoundary><L><Revenue /></L></RouteErrorBoundary>} />
        <Route path="clients" element={<RouteErrorBoundary><L><Clients /></L></RouteErrorBoundary>} />
        <Route path="clients/:id" element={<RouteErrorBoundary><L><ClientDetail /></L></RouteErrorBoundary>} />
        <Route path="pipeline" element={<Navigate to="/sales/pipeline" replace />} />
        <Route path="forecast" element={<Navigate to="/sales/forecast" replace />} />
        <Route path="data-health" element={<RouteErrorBoundary><L><DataHealth /></L></RouteErrorBoundary>} />
        <Route path="intelligence" element={<HiddenFromPartners><RouteErrorBoundary><L><Intelligence /></L></RouteErrorBoundary></HiddenFromPartners>} />
        <Route path="intelligence/models" element={<HiddenFromPartners><L><ModelInsights /></L></HiddenFromPartners>} />
        <Route path="ops" element={<L><OpsDashboard /></L>} />
        <Route path="meetings" element={<L><Meetings /></L>} />
        <Route path="actions" element={<RouteErrorBoundary><L><Actions /></L></RouteErrorBoundary>} />
        <Route path="marketing" element={<HiddenFromPartners><L><Marketing /></L></HiddenFromPartners>} />
        <Route path="audit" element={<L><Audit /></L>} />
        <Route path="trust" element={<L><Trust /></L>} />
        <Route path="settings" element={<RouteErrorBoundary><L><Settings /></L></RouteErrorBoundary>} />
        <Route path="contracts" element={<L><Contracts /></L>} />
        <Route path="plans" element={<L><AgentPlans /></L>} />
        <Route path="alerts" element={<L><Alerts /></L>} />
        <Route path="kpis" element={<L><Kpis /></L>} />
        <Route path="upgrade" element={<RouteErrorBoundary><L><Upgrade /></L></RouteErrorBoundary>} />
      </Route>

      {/* Preview routes — bypass auth; DEV-only so the sandbox never ships to production (QA P2-12). */}
      {import.meta.env.DEV && (
      <>
      {/* The /m mobile briefing without auth, for the four-state capture pass (e2e/states). */}
      <Route path="/preview/m" element={<L><MobileBriefing /></L>} />
      <Route path="/preview" element={<DashboardLayout />}>
        <Route index element={<RouteErrorBoundary><CommandCenter /></RouteErrorBoundary>} />
        <Route path="revenue" element={<RouteErrorBoundary><L><Revenue /></L></RouteErrorBoundary>} />
        <Route path="clients" element={<RouteErrorBoundary><L><Clients /></L></RouteErrorBoundary>} />
        <Route path="clients/:id" element={<RouteErrorBoundary><L><ClientDetail /></L></RouteErrorBoundary>} />
        <Route path="pipeline" element={<Navigate to="/preview/sales/pipeline" replace />} />
        <Route path="forecast" element={<Navigate to="/preview/sales/forecast" replace />} />
        <Route path="data-health" element={<RouteErrorBoundary><L><DataHealth /></L></RouteErrorBoundary>} />
        <Route path="intelligence" element={<RouteErrorBoundary><L><Intelligence /></L></RouteErrorBoundary>} />
        <Route path="intelligence/models" element={<L><ModelInsights /></L>} />
        <Route path="ops" element={<L><OpsDashboard /></L>} />
        <Route path="meetings" element={<L><Meetings /></L>} />
        <Route path="actions" element={<RouteErrorBoundary><L><Actions /></L></RouteErrorBoundary>} />
        <Route path="marketing" element={<L><Marketing /></L>} />
        <Route path="audit" element={<L><Audit /></L>} />
        <Route path="trust" element={<L><Trust /></L>} />
        <Route path="settings" element={<RouteErrorBoundary><L><Settings /></L></RouteErrorBoundary>} />
        <Route path="contracts" element={<L><Contracts /></L>} />
        <Route path="plans" element={<L><AgentPlans /></L>} />
        <Route path="alerts" element={<L><Alerts /></L>} />
        <Route path="kpis" element={<L><Kpis /></L>} />
        <Route path="upgrade" element={<RouteErrorBoundary><L><Upgrade /></L></RouteErrorBoundary>} />
      </Route>

      {/* Sales Platform Preview — bypasses auth for local dev */}
      <Route path="/preview/sales" element={<L><SalesLayout /></L>}>
        <Route index element={<L><SalesHome /></L>} />
        <Route path="pipeline" element={<L><SalesPipeline /></L>} />
        <Route path="deals" element={<Navigate to="../pipeline?view=list" replace />} />
        <Route path="deals/:dealId" element={<L><SalesDealRoom /></L>} />
        <Route path="prospects" element={<Navigate to=".." replace />} />
        <Route path="meetings" element={<L><SalesMeetings /></L>} />
        <Route path="calls" element={<L><SalesCalls /></L>} />
        <Route path="leads" element={<L><SalesLeads /></L>} />
        <Route path="book-fix" element={<L><SalesBookFix /></L>} />
        <Route path="activities" element={<Navigate to="../meetings" replace />} />
        <Route path="coaching" element={<L><SalesCoaching /></L>} />
        <Route path="forecast" element={<L><SalesForecast /></L>} />
        <Route path="leaderboard" element={<L><SalesLeaderboard /></L>} />
        <Route path="intelligence" element={<Navigate to="../coaching" replace />} />
        <Route path="settings" element={<L><SalesSettings /></L>} />
      </Route>

      {/* Tax Platform Preview — bypasses auth for local dev */}
      <Route path="/preview/tax" element={<L><TaxLayout /></L>}>
        <Route index element={<L><TaxHome /></L>} />
        <Route path="clients" element={<L><TaxClients /></L>} />
        <Route path="clients/:id" element={<L><TaxClientDetail /></L>} />
        <Route path="meetings" element={<L><TaxMeetings /></L>} />
        <Route path="follow-ups" element={<L><TaxFollowUps /></L>} />
        <Route path="deadlines" element={<L><TaxDeadlines /></L>} />
        <Route path="planning" element={<L><TaxPlanning /></L>} />
        <Route path="time" element={<L><TaxTime /></L>} />
        <Route path="intelligence" element={<L><TaxIntelligence /></L>} />
        <Route path="settings" element={<L><TaxSettings /></L>} />
      </Route>

      {/* SOW Desk Platform Preview — bypasses auth for local dev */}
      <Route path="/preview/sow" element={<L><SowLayout /></L>}>
        <Route index element={<L><SowHome /></L>} />
        <Route path="requests" element={<L><SowRequests /></L>} />
        <Route path="requests/:id" element={<L><SowRequestDetail /></L>} />
        <Route path="templates" element={<L><SowTemplates /></L>} />
        <Route path="rules" element={<L><SowRules /></L>} />
        <Route path="clauses" element={<L><SowClauses /></L>} />
        <Route path="templates/convert" element={<L><ConvertTemplate /></L>} />
        <Route path="library" element={<L><SowLibrary /></L>} />
        <Route path="renewals" element={<L><SowRenewals /></L>} />
        <Route path="reps/:email" element={<L><SowRepDetail /></L>} />
        <Route path="help" element={<L><SowHelp /></L>} />
        <Route path="intelligence" element={<L><SowIntelligence /></L>} />
        <Route path="settings" element={<L><SowSettings /></L>} />
      </Route>

      {/* Clients Platform Preview — bypasses auth for local dev */}
      <Route path="/preview/clients" element={<L><ClientsLayout /></L>}>
        <Route index element={<RouteErrorBoundary><L><ClientsHome /></L></RouteErrorBoundary>} />
        <Route path="clients" element={<RouteErrorBoundary><L><Clients /></L></RouteErrorBoundary>} />
        <Route path="clients/:id" element={<RouteErrorBoundary><L><ClientDetail /></L></RouteErrorBoundary>} />
        <Route path="meetings" element={<RouteErrorBoundary><L><Meetings /></L></RouteErrorBoundary>} />
        <Route path="follow-ups" element={<RouteErrorBoundary><L><Actions /></L></RouteErrorBoundary>} />
        <Route path="actions" element={<RouteErrorBoundary><L><Actions /></L></RouteErrorBoundary>} />
        <Route path="data-health" element={<RouteErrorBoundary><L><DataHealth /></L></RouteErrorBoundary>} />
        <Route path="intelligence" element={<RouteErrorBoundary><L><ClientsIntelligence /></L></RouteErrorBoundary>} />
        <Route path="settings" element={<RouteErrorBoundary><L><ClientsSettings /></L></RouteErrorBoundary>} />
      </Route>
      </>
      )}

      {/* Mobile-first PWA home — start_url='/m' */}
      <Route
        path="/m"
        element={
          <ProtectedRoute>
            <L><MobileBriefing /></L>
          </ProtectedRoute>
        }
      />

      <Route
        path="/build"
        element={
          <ProtectedRoute>
            <L><BuildProgress /></L>
          </ProtectedRoute>
        }
      />

      {/* Sales Platform */}
      <Route
        path="/sales"
        element={
          <ProtectedRoute>
            <L><SalesLayout /></L>
          </ProtectedRoute>
        }
      >
        <Route index element={<L><SalesHome /></L>} />
        <Route path="pipeline" element={<L><SalesPipeline /></L>} />
        <Route path="deals" element={<Navigate to="../pipeline?view=list" replace />} />
        <Route path="deals/:dealId" element={<L><SalesDealRoom /></L>} />
        <Route path="prospects" element={<Navigate to=".." replace />} />
        <Route path="meetings" element={<L><SalesMeetings /></L>} />
        <Route path="calls" element={<L><SalesCalls /></L>} />
        <Route path="leads" element={<L><SalesLeads /></L>} />
        <Route path="book-fix" element={<L><SalesBookFix /></L>} />
        <Route path="activities" element={<Navigate to="../meetings" replace />} />
        <Route path="coaching" element={<L><SalesCoaching /></L>} />
        <Route path="forecast" element={<L><SalesForecast /></L>} />
        <Route path="leaderboard" element={<L><SalesLeaderboard /></L>} />
        <Route path="intelligence" element={<Navigate to="../coaching" replace />} />
        <Route path="settings" element={<L><SalesSettings /></L>} />
        {/* Workspace settings (Billing · Team · Integrations) inside this shell —
            a standalone Sales workspace is pinned here and has no /dashboard. */}
        <Route path="settings/workspace" element={<RouteErrorBoundary><L><Settings variant="workspace" /></L></RouteErrorBoundary>} />
        <Route path="upgrade" element={<RouteErrorBoundary><L><Upgrade /></L></RouteErrorBoundary>} />
      </Route>

      {/* Tax Platform — gated behind the per-workspace tax_enabled add-on */}
      <Route
        path="/tax"
        element={
          <ProtectedRoute>
            <TaxGate>
              <L><TaxLayout /></L>
            </TaxGate>
          </ProtectedRoute>
        }
      >
        <Route index element={<L><TaxHome /></L>} />
        <Route path="clients" element={<L><TaxClients /></L>} />
        <Route path="clients/:id" element={<L><TaxClientDetail /></L>} />
        <Route path="meetings" element={<L><TaxMeetings /></L>} />
        <Route path="follow-ups" element={<L><TaxFollowUps /></L>} />
        <Route path="deadlines" element={<L><TaxDeadlines /></L>} />
        <Route path="planning" element={<L><TaxPlanning /></L>} />
        <Route path="time" element={<L><TaxTime /></L>} />
        <Route path="intelligence" element={<L><TaxIntelligence /></L>} />
        <Route path="settings" element={<L><TaxSettings /></L>} />
      </Route>

      {/* SOW Desk Platform — gated behind the per-workspace sow_enabled add-on */}
      <Route
        path="/sow"
        element={
          <ProtectedRoute>
            <SowGate>
              <L><SowLayout /></L>
            </SowGate>
          </ProtectedRoute>
        }
      >
        <Route index element={<L><SowHome /></L>} />
        <Route path="requests" element={<L><SowRequests /></L>} />
        <Route path="requests/:id" element={<L><SowRequestDetail /></L>} />
        <Route path="templates" element={<L><SowTemplates /></L>} />
        <Route path="rules" element={<L><SowRules /></L>} />
        <Route path="clauses" element={<L><SowClauses /></L>} />
        <Route path="templates/convert" element={<L><ConvertTemplate /></L>} />
        <Route path="library" element={<L><SowLibrary /></L>} />
        <Route path="renewals" element={<L><SowRenewals /></L>} />
        <Route path="reps/:email" element={<L><SowRepDetail /></L>} />
        <Route path="help" element={<L><SowHelp /></L>} />
        <Route path="intelligence" element={<L><SowIntelligence /></L>} />
        <Route path="settings" element={<L><SowSettings /></L>} />
        {/* Workspace settings (Billing · Team) — a SOW Desk workspace has no /dashboard. */}
        <Route path="settings/workspace" element={<RouteErrorBoundary><L><Settings variant="workspace" /></L></RouteErrorBoundary>} />
      </Route>

      {/* Clients Platform — core (NOT gated). Members assigned platform_type
          'clients' are pinned here by PlatformGuard. Reuses the owner hub
          pages; ClientDetail nav resolves under /clients via useBasePath. */}
      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            <L><ClientsLayout /></L>
          </ProtectedRoute>
        }
      >
        <Route index element={<RouteErrorBoundary><L><ClientsHome /></L></RouteErrorBoundary>} />
        <Route path="clients" element={<RouteErrorBoundary><L><Clients /></L></RouteErrorBoundary>} />
        <Route path="clients/:id" element={<RouteErrorBoundary><L><ClientDetail /></L></RouteErrorBoundary>} />
        <Route path="meetings" element={<RouteErrorBoundary><L><Meetings /></L></RouteErrorBoundary>} />
        <Route path="follow-ups" element={<RouteErrorBoundary><L><Actions /></L></RouteErrorBoundary>} />
        {/* alias: reused pages (Meetings, FollowUpsWidget) deep-link to `${base}/actions` */}
        <Route path="actions" element={<RouteErrorBoundary><L><Actions /></L></RouteErrorBoundary>} />
        <Route path="data-health" element={<RouteErrorBoundary><L><DataHealth /></L></RouteErrorBoundary>} />
        <Route path="intelligence" element={<HiddenFromPartners to="/clients"><RouteErrorBoundary><L><ClientsIntelligence /></L></RouteErrorBoundary></HiddenFromPartners>} />
        <Route path="settings" element={<RouteErrorBoundary><L><ClientsSettings /></L></RouteErrorBoundary>} />
        {/* Workspace settings (Billing · Team · Integrations) inside this shell —
            a standalone Clients workspace is pinned here and has no /dashboard. */}
        <Route path="settings/workspace" element={<RouteErrorBoundary><L><Settings variant="workspace" /></L></RouteErrorBoundary>} />
        <Route path="upgrade" element={<RouteErrorBoundary><L><Upgrade /></L></RouteErrorBoundary>} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<L><NotFound /></L>} />
    </Routes>
    </ViewScopeProvider>
    </ImpersonationProvider>
    </ErrorBoundary>
  );
}
